import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase-config';
import { BlogPost } from '../types/blog-admin';

export function useBlogPosts() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const q = query(
          collection(db, 'blogPosts'),
          where('status', '==', 'published'),
          orderBy('publishedAt', 'desc')
        );
        const snapshot = await getDocs(q);
        const postsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as BlogPost[];
        setPosts(postsData);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load blog posts'));
        console.error('Error fetching blog posts:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  return { posts, loading, error };
}

export function useBlogPost(slug: string) {
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        // Query by slug
        const q = query(
          collection(db, 'blogPosts'),
          where('slug', '==', slug),
          where('status', '==', 'published')
        );
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setPost(null);
        } else {
          const postDoc = snapshot.docs[0];
          const postData = {
            id: postDoc.id,
            ...postDoc.data()
          } as BlogPost;
          setPost(postData);

          // Increment view count
          try {
            await updateDoc(doc(db, 'blogPosts', postDoc.id), {
              views: increment(1)
            });
          } catch (viewError) {
            // Don't fail if view count update fails
            console.error('Error updating view count:', viewError);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load blog post'));
        console.error('Error fetching blog post:', err);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPost();
    }
  }, [slug]);

  return { post, loading, error };
}
